import { useEffect, useState } from "react";
import { X, Share, Plus, ArrowDown } from "lucide-react";

const KEY = "pwa-nudge-dismissed";

function isIOS() {
  const ua = window.navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}
function isStandalone() {
  return window.navigator.standalone === true ||
         window.matchMedia("(display-mode: standalone)").matches;
}

/**
 * iOS PWA install nudge. iOS Safari doesn't allow apps to programmatically
 * trigger "Add to Home Screen" — it MUST come from Safari's toolbar share
 * sheet. So this component is deliberately instructional: numbered steps and
 * an animated arrow that points down at Safari's bottom bar so the user knows
 * to tap Safari's share button (not the icon on this card).
 */
export default function PWANudge() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!isIOS() || isStandalone() || localStorage.getItem(KEY)) return;
    const t = setTimeout(() => setShow(true), 2500);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    localStorage.setItem(KEY, "1");
    setShow(false);
  };

  if (!show) return null;
  return (
    <div className="fixed left-3 right-3 bottom-24 z-50 md:hidden" data-testid="pwa-nudge">
      <div className="rounded-2xl bg-card border border-emerald-500/40 shadow-2xl p-4 relative">
        <button onClick={dismiss}
                data-testid="pwa-nudge-dismiss"
                aria-label="Dismiss install prompt"
                className="absolute top-2 right-2 text-muted-foreground p-1.5 rounded-full hover:bg-secondary">
          <X className="w-3.5 h-3.5" />
        </button>

        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center shrink-0">
            <Plus className="w-4 h-4 text-emerald-400" />
          </div>
          <div className="font-display font-semibold text-sm">Install Aanar on your iPhone</div>
        </div>

        <ol className="space-y-1.5 text-[13px] text-muted-foreground pl-1">
          <li className="flex items-start gap-2">
            <span className="w-4 h-4 rounded-full bg-secondary text-foreground text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">1</span>
            <span>
              Tap the{" "}
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-secondary text-foreground font-medium">
                <Share className="w-3 h-3" /> Share
              </span>{" "}
              button in Safari's bottom bar
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-4 h-4 rounded-full bg-secondary text-foreground text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">2</span>
            <span>Scroll and pick <span className="font-medium text-foreground">Add to Home Screen</span></span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-4 h-4 rounded-full bg-secondary text-foreground text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">3</span>
            <span>Tap <span className="font-medium text-foreground">Add</span> — done</span>
          </li>
        </ol>

        {/* Animated arrow pointing down at Safari's toolbar share button.
            Position it toward the bottom-center where Safari's bar lives. */}
        <div className="flex justify-center mt-3 opacity-80">
          <ArrowDown className="w-5 h-5 text-emerald-400 animate-bounce" data-testid="pwa-nudge-arrow" />
        </div>
        <div className="text-[10px] text-muted-foreground text-center mt-1">
          Safari's share button is at the bottom of your screen
        </div>
      </div>
    </div>
  );
}
